import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Theme,
  Typography,
} from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { IEditNick, IUser } from '../../../../api/types/user'
import { useForm } from 'react-hook-form'
import { securedAxios } from '../../../../axios'
import { API_URL, BACKEND_URL } from '../../../../config'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { mutationDeleteCharacter, mutationEditUserNick } from '../../../../api/mutations'
import { useGlobalError } from '../../../../context/GlobalError'
import { ConfirmationDialog } from '../../../../components/ConfirmationDialog/ConfirmationDialog'
import { extractMessage } from '../../../../utils'

import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'
import { characterQueryKey, userQueryKey } from '../../../../api/queries'

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    changeNick: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    avatarRow: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    userCard: {
      margin: theme.spacing(0.5),
    },
    avatar: {
      marginRight: '8px',
    },
    uploadBox: {
      flexGrow: 0,
      width: '100px',
    },
    fileBox: {},
    nickInput: {
      flexGrow: 1,
      paddingRight: '8px',
    },
    rows: {
      display: 'flex',
      flexDirection: 'column',
    },
    row: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      marginTop: '8px',
      padding: '6px',
      borderStyle: 'solid',
      borderWidth: '1px',
      borderRadius: 4,
      borderColor: theme.palette.grey[600],
    },
    rowLeft: {
      paddingRight: '8px',
    },
    chips: {
      '& div': {
        marginRight: '4px',
      },
      marginTop: '8px',
      display: 'flex',
      justifyContent: 'center',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '10px',
    },
  })
)

interface Props {
  user: IUser
}

export const UserCard: React.FC<Props> = ({ user }) => {
  const classes = useStyles()
  const { addError } = useGlobalError()
  const queryClient = useQueryClient()

  const { register, handleSubmit } = useForm<any>()
  const onSubmit = (data: any) => {
    const formData = new FormData()
    formData.append('file', data.file[0])
    securedAxios
      .post('/users/' + user?.id + '/upload_avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(() => window.location.reload())
  }
  const {
    register: registerNC,
    handleSubmit: handleSubmitNC,
    errors: errorsNC,
  } = useForm<IEditNick>()

  const { mutate: editNickMutation, isLoading: isChangingNick } = useMutation(
    (data: IEditNick) => mutationEditUserNick(user.id, data),
    {
      onError: (err: any) => {
        addError(extractMessage(err) || 'Nie udało się zmienić nicku', err?.status)
      },
      onSuccess: async () => {
        queryClient.invalidateQueries(userQueryKey(Number(user.id)))
        user.character_id && queryClient.invalidateQueries(characterQueryKey(user.character_id))
      },
    }
  )
  const handleEditNick = (data: IEditNick) => editNickMutation(data)

  const { mutate: deleteCharMutation, isLoading: isDeletingChar } = useMutation(
    () => mutationDeleteCharacter(user.character_id || 0),
    {
      onError: (err: any) => {
        addError(extractMessage(err) || 'Nie udało się usunąć postaci', err?.status)
      },
      onSuccess: async () => {
        queryClient.invalidateQueries(userQueryKey(Number(user.id)))
      },
    }
  )

  return (
    <Card className={classes.userCard}>
      <CardContent>
        <div className={classes.header}>
          <Box sx={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
            <Avatar
              className={classes.avatar}
              src={user?.avatar ? BACKEND_URL + user?.avatar : ''}
              alt="avatar"
            />
            <Typography variant="h5" component="h2">
              {user?.username}
            </Typography>
          </Box>

          <div className={classes.chips}>
            <Chip
              icon={user?.admin ? <CheckOutlinedIcon /> : <ClearOutlinedIcon />}
              label="Admin"
              variant={'outlined'}
              size={'small'}
              color={user?.admin ? 'success' : 'error'}
            />
            <Chip
              icon={user?.gm ? <CheckOutlinedIcon /> : <ClearOutlinedIcon />}
              label="GM"
              variant={'outlined'}
              size={'small'}
              color={user?.gm ? 'success' : 'error'}
            />
          </div>
        </div>

        <div className={classes.rows}>
          <div className={classes.row}>
            <div className={classes.rowLeft}>
              <Typography variant="caption" display="block" gutterBottom>
                Zmień avatar
              </Typography>
            </div>
            <div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className={classes.avatarRow}>
                  <div className={classes.fileBox}>
                    <input ref={register} type="file" name="file" />
                  </div>
                  <div className={classes.uploadBox}>
                    <Button
                      style={{ width: '100%' }}
                      variant="outlined"
                      color="primary"
                      type="submit"
                    >
                      Wyślij
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.rowLeft}>
              <Typography variant="caption" display="block" gutterBottom>
                Zmień nick
              </Typography>
            </div>
            <div>
              <form onSubmit={handleSubmitNC(handleEditNick)}>
                <div className={classes.changeNick}>
                  <div className={classes.nickInput}>
                    <TextField
                      name="username"
                      fullWidth={true}
                      style={{ marginRight: '6px' }}
                      variant="standard"
                      defaultValue={user.username}
                      inputProps={{
                        ref: registerNC({
                          required: 'Wymagane',
                        }),
                      }}
                      error={!!errorsNC.username}
                      helperText={errorsNC.username && errorsNC.username.message}
                    />
                  </div>
                  <div className={classes.uploadBox}>
                    <Button
                      style={{ width: '100%' }}
                      variant="outlined"
                      color="primary"
                      type="submit"
                      disabled={isChangingNick}
                    >
                      Zmień
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {user.character_id && (
            <div style={{ marginTop: '8px' }}>
              <ConfirmationDialog
                isLoading={isDeletingChar}
                body={
                  <div>
                    Na pewno usunąć postać? Nie da się jej odzyskać, trzeba będzie stworzyć od nowa.
                  </div>
                }
              >
                {(confirm: any) => (
                  <Button
                    style={{ width: '100%' }}
                    disabled={isDeletingChar}
                    onClick={confirm(
                      (e: any) => () =>
                        new Promise(r => {
                          deleteCharMutation()
                          return r('ok')
                        })
                    )}
                    variant="outlined"
                    color="secondary"
                  >
                    Usuń postać
                  </Button>
                )}
              </ConfirmationDialog>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
